// cookieChoice.ts — CORE-1659
//
// Выбор по категориям cookie. Чистый модуль: без React, стораджа и сети.
//
// Человек решает по двум категориям — аналитические и маркетинговые. Необходимые
// и функциональные (настройки на устройстве) работают всегда и в выборе не
// участвуют, поэтому в записи их нет.
//
// Одна и та же строка лежит в сторадже браузера и в `UserConsents.Value` на
// сервере: «analytics=yes;marketing=no». До CORE-1659 решение было одно на всё
// («accepted» / «declined») — такие значения читаются как «всё разрешено» и
// «всё запрещено», и человека повторно не спрашивают.

export type CookieCategory = 'analytics' | 'marketing';

export interface CookieChoice {
  analytics: boolean;
  marketing: boolean;
}

export const COOKIE_CATEGORIES: readonly CookieCategory[] = ['analytics', 'marketing'];

export const ALL_COOKIES: CookieChoice = { analytics: true, marketing: true };
export const NECESSARY_COOKIES_ONLY: CookieChoice = { analytics: false, marketing: false };

/** Прежнее решение «одно на всё» — так его шлёт и хранит сборка до CORE-1659. */
export type LegacyCookieDecision = 'accepted' | 'declined';

export function isLegacyCookieDecision(raw: unknown): raw is LegacyCookieDecision {
  return raw === 'accepted' || raw === 'declined';
}

/** Разобрать сохранённое значение. Непонятное — `null`: человека спросят заново. */
export function parseCookieChoice(raw: string | null | undefined): CookieChoice | null {
  if (!raw) return null;
  if (raw === 'accepted') return { ...ALL_COOKIES };
  if (raw === 'declined') return { ...NECESSARY_COOKIES_ONLY };

  const found: Partial<CookieChoice> = {};
  for (const part of raw.split(';')) {
    const [key, value, ...rest] = part.split('=');
    if (rest.length || (key !== 'analytics' && key !== 'marketing')) return null;
    if (value !== 'yes' && value !== 'no') return null;
    if (key in found) return null;
    found[key] = value === 'yes';
  }
  if (found.analytics === undefined || found.marketing === undefined) return null;
  return { analytics: found.analytics, marketing: found.marketing };
}

export function serializeCookieChoice(choice: CookieChoice): string {
  return `analytics=${choice.analytics ? 'yes' : 'no'};marketing=${choice.marketing ? 'yes' : 'no'}`;
}

export function sameCookieChoice(
  a: CookieChoice | null | undefined,
  b: CookieChoice | null | undefined,
): boolean {
  if (!a || !b) return !a && !b;
  return a.analytics === b.analytics && a.marketing === b.marketing;
}
